import { apiFetchUrl, apiUrl, apiFetch, unwrap } from '@shared/http/client';
import type { DataResult } from '@shared/http/client';
import { applyTheme } from '@shared/composables/useTheme';
import { initApp } from '@shared/composables/useAppInit';
import { useOrganizationStore } from '@shared/stores/organization';
import type { Organization } from '@shared/stores/organization';
import { useUserStore } from '@shared/stores/user';

/**
 * 1:1 Port von `GuestAuthenticationService` + dem Organisations-Zweig, den Angular als Seiteneffekt
 * von `userCredentials$` fährt (`OrganizationService.loginOrganization$`).
 *
 * Warum NICHT `initApp()` aus shared: das lädt zusätzlich `GET /users`, und dieser Endpunkt
 * antwortet für ein anonymes Gast-Token mit **403** ("No club member login context entity found in
 * token storage"). `useAppInit` behandelt 401/403 mit automatischem Logout plus Weiterleitung auf
 * die Fehlerseite — der Gast würde sich also selbst aussperren. Angular hat das Problem nicht, weil
 * `UserService` auf `credentials.userId` gatet und das Gast-JWT `userId: null` trägt.
 */

/**
 * Angular: `http.get(..., { responseType: 'text' })` — der Endpunkt liefert das rohe JWT, kein JSON.
 * Deshalb `apiFetchUrl` statt `apiFetch`; die URL wird wie dort aus `apiHost` **und** `apiPath`
 * gebaut (Angulars `config.apiUrl`), `apiUrl()` allein kennt den Pfad-Praefix nicht.
 */
async function fetchAnonymousGuestToken(clubId: number): Promise<string> {
    const { apiPath } = window.kgrDynamicConfig;
    const response = await apiFetchUrl(apiUrl(`${apiPath}/auth/club/${clubId}/anonymous-guest/`));
    return (await response.text()).trim();
}

/**
 * 1:1 zu `GuestAuthenticationService.loginAsAnonymousGuest()`: holt **bedingungslos** ein frisches
 * Gast-Token und macht es zum aktiven.
 *
 * Das Organisationsprofil gehoert dazu, auch wenn der Angular-Service es nicht selbst laedt: dort
 * haengt `OrganizationService.loginOrganization$` an `userCredentials$` und feuert bei JEDEM
 * Credential-Wechsel mit. Ohne diesen Nachzug fehlt der Vereinskopf an jeder Stelle, die nach dem
 * Gast-Login keine weitere Route mit eigenem Guard ansteuert — gemessen am Institutions-Redirect
 * ohne `target`, der auf `/` bleibt.
 */
export async function loginAsAnonymousGuest(clubId: number): Promise<void> {
    const userStore = useUserStore();
    const token = await fetchAnonymousGuestToken(clubId);
    // persist: false — siehe ensureClubCredentials().
    userStore.setToken(token, false);
    userStore.setInstitutionId(clubId, false);
    await loadOrganizationProfile(clubId);
}

async function loadOrganizationProfile(clubId: number): Promise<void> {
    const result = await apiFetch<DataResult<Organization>>(`/organizations/${clubId}/profile`);
    // Das individuelle Theme kommt als verschachtelte `{ data: … }`-Hülle (Fractal-Item-Include).
    const theme = unwrap<Record<string, string>>(result.data.theme);
    useOrganizationStore().setOrganization({ ...result.data, theme });
    applyTheme(theme);
}

/**
 * Stellt sicher, dass Credentials für genau diesen Verein vorliegen — das Pendant zu
 * `RequireClubUserCredentialsGuard.canActivate()`. Passt die aktuelle `institutionId` nicht,
 * wird ein frisches Gast-Token geholt (auch dann, wenn bereits ein Member-Login aktiv ist:
 * Angular vergleicht ebenfalls nur die Institution).
 */
export async function ensureClubCredentials(clubId: number): Promise<boolean> {
    const userStore = useUserStore();
    if (userStore.institutionId === clubId && userStore.token) {
        // Angular's UserService fetches the users for every credential change that carries a
        // userId — including the persisted member token restored on reload. Without this the
        // member session survives F5 but `currentUser` stays empty (no login status, guest UI).
        if (userStore.mainUserId && !userStore.currentUser) {
            await initApp();
        } else if (!useOrganizationStore().organization) {
            await loadOrganizationProfile(clubId);
        }
        return true;
    }

    // persist: false — der Gast-Token lebt bei Angular ausschliesslich im UserCredentialsStore,
    // also im Speicher. Im Third-Party-iFrame ist das ausserdem die einzige verlaessliche Ablage.
    await loginAsAnonymousGuest(clubId);
    return useUserStore().institutionId === clubId;
}
